// --- helpers ---------------------------------------------------------------

function stripSpaces(value) {
  return String(value || '').replace(/\s+/g, '');
}

function splitName(full) {
  const parts = String(full || '')
    .trim()
    .split(/\s+/);
  if (parts.length === 0) return { first: '', last: '' };
  const [first, ...rest] = parts;
  return {
    first: first || '',
    last: (rest.join(' ') || '').trim(),
  };
}

function fmtYYYYMMtoYYYYMMDD01(obj /* {year,month} */) {
  const y = obj?.year?.toString().padStart(4, '0');
  const m = obj?.month?.toString().padStart(2, '0');
  if (y && m && /^\d{4}$/.test(y) && /^(0[1-9]|1[0-2])$/.test(m)) {
    return `${y}-${m}-01`;
  }
  return '';
}

function pickDOBFromMainDriver(details) {
  // Prefer ISO string "YYYY-MM-DD" if they have birthDateString
  if (
    details?.birthDateString &&
    /^\d{4}-\d{2}-\d{2}$/.test(details.birthDateString)
  ) {
    return details.birthDateString;
  }
  // Or an object { year, month, day }
  const y = details?.birthDate?.year;
  const m = details?.birthDate?.month;
  const d = details?.birthDate?.day;
  if (y && m && d) {
    const yy = String(y).padStart(4, '0');
    const mm = String(m).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }
  return '';
}

function normalizeName(s) {
  if (!s) return '';
  // remove diacritics, punctuation, collapse spaces, lowercase
  const noDiacritics =
    s.normalize?.('NFD').replace(/[\u0300-\u036f]/g, '') || s;
  return noDiacritics
    .toLowerCase()
    .replace(/[^a-z\s'-]/g, ' ') // keep letters/spaces/basic name punctuation
    .replace(/\s+/g, ' ') // collapse spaces
    .trim();
}

function pickLicenseFromMainDriver(details) {
  // {year,month,day?} object
  const y = details?.licenseFirstIssueDate?.year;
  const m = details?.licenseFirstIssueDate?.month;
  if (y && m) return { year: String(y), month: String(m) };
  return null;
}

function pickDriverSource(fd) {
  // 1) mainDriverDetails present → use it
  if (fd?.mainDriverDetails && typeof fd.mainDriverDetails === 'object') {
    const name = splitName(fd.mainDriverDetails.fullName || '');
    const dob = pickDOBFromMainDriver(fd.mainDriverDetails);
    const lic = pickLicenseFromMainDriver(fd.mainDriverDetails);
    // support numbers (0) or strings ("Never", "7+")
    const uci =
      fd.mainDriverDetails.uninterruptedCarInsurance ??
      fd.uninterruptedCarInsurance ??
      null;
    const lcy =
      fd.mainDriverDetails.lastClaimYears ?? fd.lastClaimYears ?? null;

    return {
      source: 'mainDriverDetails',
      firstName: name.first,
      lastName: name.last,
      gender: fd.mainDriverDetails.gender || '',
      dateOfBirth: dob,
      maritalStatus: fd.mainDriverDetails.maritalStatus || '',
      licenseFirstIssue: lic,
      uninterruptedCarInsurance: uci,
      lastClaimYears: lcy,
    };
  }

  // 2) applicant is the main driver
  if (String(fd?.mainDriver).toLowerCase() === 'yes') {
    const name = splitName(fd.fullName || '');
    const { dateOfBirth, gender } = deriveDateOfBirthAndGender(fd.idNumber);

    return {
      source: 'applicant',
      firstName: name.first,
      lastName: name.last,
      gender: gender || fd.gender || '',
      dateOfBirth: dateOfBirth || '',
      maritalStatus: fd.maritalStatus || '',
      licenseFirstIssue: fd.licenseFirstIssue || null, // {year,month}
      uninterruptedCarInsurance: fd.uninterruptedCarInsurance ?? null,
      lastClaimYears: fd.lastClaimYears ?? null,
    };
  }

  // 3) additional driver path (mainDriver === "No")
  const addName = splitName(fd.additionalDriverFullName || '');
  const addDob = fd.additionalDriverBirthday
    ? `${String(fd.additionalDriverBirthday.year || '').padStart(4, '0')}-${String(fd.additionalDriverBirthday.month || '').padStart(2, '0')}-${String(fd.additionalDriverBirthday.day || '').padStart(2, '0')}`
    : '';

  return {
    source: 'additional',
    firstName: addName.first,
    lastName: addName.last,
    gender: fd.additionalDriverGender || '',
    dateOfBirth: addDob,
    maritalStatus: fd.additionalDriverMaritalStatus || '',
    licenseFirstIssue: fd.additionalDriverLicenseFirstIssue || null, // {year,month}
    uninterruptedCarInsurance:
      fd.additionalDriverUninterruptedCarInsurance ?? null,
    lastClaimYears: fd.additionalDriverLastClaimYears ?? null,
  };
}

const PARKING_TYPE_OPTIONS = [
  { Text: 'Locked Garage', Value: 'LockedGarage' },
  { Text: 'Open Parking Lot', Value: 'ParkingLot' },
  { Text: 'Access Controlled Area', Value: 'AccessessControlled' },
  { Text: 'On Pavement / in Street', Value: 'PavementStreet' },
  { Text: 'Yard - No Locked Gates', Value: 'YardNoLockedGates' },
  { Text: 'Yard - With Locked Gates', Value: 'YardWithLockedGates' },
  { Text: 'Basement - Electronic Access', Value: 'BasementElectronicAccess' },
  {
    Text: 'Basement - No Electronic Access',
    Value: 'BasementNoElectronicAccess',
  },
];

const PARKING_VALUE_TO_TEXT = new Map(
  PARKING_TYPE_OPTIONS.map(({ Value, Text }) => [Value.toLowerCase(), Text])
);

function getParkingValueByText(text) {
  const match = PARKING_TYPE_OPTIONS.find((option) => option.Text === text);
  return match ? match.Value : null;
}

// Converts API enum values (e.g. "YardWithLockedGates") to display text.
// Already-text values pass through unchanged.
export function normalizeParkingType(raw) {
  if (!raw) return '';
  return PARKING_VALUE_TO_TEXT.get(String(raw).trim().toLowerCase()) || raw;
}

function normalizeVehicleUse(carUse) {
  const value = String(carUse || '').trim();
  const allowed = ['Personal', 'Occasional Business Use', 'Full Business Use'];

  return allowed.includes(value) ? value : '';
}

// Maps the vehicle-use answer to its API code:
//   Personal → "P", Occasional Business Use → "OB", Full Business Use → "B".
function mapVehicleUseCode(carUse) {
  const codes = {
    Personal: 'P',
    'Occasional Business Use': 'OB',
    'Full Business Use': 'B',
  };

  return codes[String(carUse || '').trim()] || '';
}

// Maps a segmented-scale year answer (Never, <1, 1, 2, 3, 4, 5, 6, 7+) to its
// API code: "A" for Never, "0" for <1, and "0"–"7" for the numeric options
// ("7+" → "7"). Returns "" for an unanswered question.
function mapInsuranceYearsCode(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  if (v.toLowerCase() === 'never') return 'A';
  if (v === '<1') return '0';
  if (v === '7+') return '7';

  const parsed = parseInt(v, 10);
  if (isNaN(parsed)) return '';

  return String(Math.min(Math.max(parsed, 0), 7));
}

function deriveDateOfBirthAndGender(idNumber) {
  if (!idNumber || idNumber.length < 6) {
    return { dateOfBirth: '', gender: '' };
  }

  const yy = idNumber.substring(0, 2);
  const mm = idNumber.substring(2, 4);
  const dd = idNumber.substring(4, 6);

  const currentYear = new Date().getFullYear() % 100;
  const century = parseInt(yy, 10) <= currentYear ? '20' : '19';
  const dateOfBirth = `${century}${yy}-${mm}-${dd}`;

  const genderDigit = parseInt(idNumber.charAt(6), 10);
  const gender = genderDigit >= 5 ? 'Male' : 'Female';

  return { dateOfBirth, gender };
}

export function calculateNCB(uninterruptedCarInsurance, lastClaimYears) {
  // No insurance history ("Never" / "No History" / "A" / empty) → NCB is "A".
  const insuranceStr = String(uninterruptedCarInsurance ?? '')
    .trim()
    .toLowerCase();
  if (
    insuranceStr === '' ||
    insuranceStr === 'never' ||
    insuranceStr === 'no history' ||
    insuranceStr === 'a'
  ) {
    return 'A';
  }

  // Parse a segmented-scale value into a number of years.
  // "<1" → 0, "7+" → 7, "Never"/empty → `neverValue`.
  const parseYears = (val, neverValue) => {
    const str = String(val ?? '')
      .trim()
      .toLowerCase();
    if (str === '' || str === 'never') return neverValue;
    if (str === '<1') return 0;
    if (str === '7+') return 7;
    const parsed = parseInt(str, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  const insuranceYears = parseYears(uninterruptedCarInsurance, 0);
  // "Never" claimed (or no claim recorded) → effectively infinite years since
  // the last claim, so `min` falls back to the uninterrupted-insurance years.
  const claimYears = parseYears(lastClaimYears, Infinity);

  // NCB is the lower of the two, so it can never exceed either value.
  return String(Math.min(insuranceYears, claimYears));
}

// Motorcycle only collects a single "years claim free" answer, which is the
// NCB itself. Just normalize the "7+" scale value to "7".
export function normalizeBikeNCB(yearsClaimFree) {
  const str = String(yearsClaimFree ?? '').trim();
  return str === '7+' ? '7' : str;
}

export function isEmptyObject(obj) {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0
  );
}

/**
 * True when a quote API response contains no usable quotes. Covers:
 *  - a non-array / empty response
 *  - a 200 response whose payload is a failure marker, e.g.
 *    [{ status: 'failed', error: { CustomMessage, ExceptionMessage, ... } }]
 * Quote endpoints can return HTTP 200 with a failure body, so a 200 alone
 * does not mean success.
 */
export function hasNoQuotes(data) {
  return (
    !Array.isArray(data) ||
    data.length === 0 ||
    data.every((item) => item?.status === 'failed' || item?.error != null)
  );
}

/**
 * Call centre operating hours, keyed by day-of-week (0 = Sunday … 6 = Saturday).
 * A `null` close hour means the centre is closed for that day.
 *
 *   Monday – Thursday : 08:00 – 19:00  (kept at 20:00 below to preserve the
 *                                       existing weekday behaviour)
 *   Friday            : 08:00 – 17:00
 *   Saturday          : 08:00 – 13:00
 *   Sunday            : closed
 *
 * Public holidays (fetched from the Nager.Date API) override the day-of-week
 * rule above with 08:00 – 13:00, the same hours as a Saturday. Christmas Day
 * (25 Dec) and New Year's Day (1 Jan) are the exception to that exception –
 * they're treated as closed, exactly like a Sunday.
 */
const CALL_CENTRE_OPEN_HOUR = 8;

const PUBLIC_HOLIDAY_COUNTRY_CODE = 'ZA';
// Single storage key – always overwritten with whatever the current year's
// holidays are, rather than accumulating one key per year forever.
const PUBLIC_HOLIDAY_CACHE_KEY = 'publicHolidays';

// year (number) -> Set<'YYYY-MM-DD'>
const publicHolidayDatesByYear = {};
// year (number) -> in-flight fetch promise, so concurrent lookups share one request
const publicHolidayFetchPromises = {};

function readCachedHolidaysFromStorage(year) {
  try {
    const raw = window?.localStorage?.getItem(PUBLIC_HOLIDAY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Only reuse the cache when it's actually for the year being asked about –
    // e.g. a next-year preload shouldn't be satisfied by last year's cache.
    if (!parsed || parsed.year !== year || !Array.isArray(parsed.dates)) {
      return null;
    }
    return new Set(parsed.dates);
  } catch {
    return null;
  }
}

function writeCachedHolidaysToStorage(year, dates) {
  // Only persist the real current year – a next-year preload stays in-memory
  // only, so storage always overwrites with (and only ever reflects) the
  // current year rather than piling up one entry per year.
  if (year !== new Date().getFullYear()) return;
  try {
    window?.localStorage?.setItem(
      PUBLIC_HOLIDAY_CACHE_KEY,
      JSON.stringify({ year, dates: [...dates] })
    );
  } catch {
    // Ignore storage errors (private browsing, quota, SSR, etc.)
  }
}

async function fetchPublicHolidayDates(year) {
  const response = await fetch(
    `https://date.nager.at/api/v3/PublicHolidays/${year}/${PUBLIC_HOLIDAY_COUNTRY_CODE}`
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const holidays = await response.json();
  return new Set(holidays.map((h) => h.date)); // API returns "date": "YYYY-MM-DD"
}

/**
 * Kicks off (once per year) fetching + caching that year's public holidays.
 * Safe to call repeatedly – concurrent calls share the same in-flight request,
 * and a browser cache (localStorage) avoids re-fetching on every page load.
 * Failures degrade to "no known holidays" rather than blocking the call
 * centre hours logic.
 */
function ensurePublicHolidaysLoaded(year) {
  if (publicHolidayDatesByYear[year]) return publicHolidayFetchPromises[year];
  if (publicHolidayFetchPromises[year]) return publicHolidayFetchPromises[year];

  const cached = readCachedHolidaysFromStorage(year);
  if (cached) {
    publicHolidayDatesByYear[year] = cached;
    return Promise.resolve();
  }

  publicHolidayFetchPromises[year] = fetchPublicHolidayDates(year)
    .then((dates) => {
      publicHolidayDatesByYear[year] = dates;
      writeCachedHolidaysToStorage(year, dates);
    })
    .catch((error) => {
      console.error('Error fetching public holidays:', error);
      publicHolidayDatesByYear[year] = new Set();
    });

  return publicHolidayFetchPromises[year];
}

function toDateKey(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Synchronous holiday lookup. If the given year hasn't been fetched yet, this
 * triggers a background fetch (for next time) and returns `false` for now –
 * the call centre falls back to normal day-of-week hours until the holiday
 * list is available.
 */
function isPublicHoliday(date) {
  const year = date.getFullYear();
  const dates = publicHolidayDatesByYear[year];
  if (!dates) {
    ensurePublicHolidaysLoaded(year);
    return false;
  }
  return dates.has(toDateKey(date));
}

/**
 * Christmas Day and New Year's Day are always treated as closed (like a
 * Sunday), regardless of what the public holiday API says or which weekday
 * they fall on.
 */
function isChristmasOrNewYear(date) {
  const month = date.getMonth(); // 0-indexed
  const day = date.getDate();
  return (month === 11 && day === 25) || (month === 0 && day === 1);
}

// TEMP (manual testing only): set to a local ISO string (no trailing 'Z') to
// simulate a specific day/time, or null to use the real clock. Revert before commit.
//   '2026-07-06T10:00' -> Mon, in hours (weekday, unchanged behaviour)
//   '2026-07-09T10:00' -> Thu, tomorrow = Fri (caps at 17:00)
//   '2026-07-10T10:00' -> Fri, tomorrow = Sat (caps at 13:00)
//   '2026-07-10T18:00' -> Fri after close (no "Right Now")
//   '2026-07-11T09:00' -> Sat morning, open (slots up to 13:00)
//   '2026-07-11T14:00' -> Sat after 13:00 -> "Call Me Monday"
//   '2026-07-12T09:00' -> Sun, closed -> "Call Me Tomorrow" = Mon
const TEST_NOW = null;
function getNow() {
  return TEST_NOW ? new Date(TEST_NOW) : new Date();
}

// Kick off fetching the current and next year's public holidays as soon as
// this module loads, so the cache is warm by the time a callback modal opens.
// (Next year is preloaded too so a lookup rolling over New Year's Eve isn't
// stuck falling back to "no known holidays".)
if (typeof window !== 'undefined') {
  const currentYear = new Date().getFullYear();
  ensurePublicHolidaysLoaded(currentYear);
  ensurePublicHolidaysLoaded(currentYear + 1);
}

function getCallCentreCloseHour(date) {
  if (isChristmasOrNewYear(date)) return null; // closed, like a Sunday

  const day = date.getDay();
  if (day === 0) return null; // Sunday – closed

  if (isPublicHoliday(date)) return 13; // public holiday: 08:00 – 13:00

  switch (day) {
    case 5: // Friday
      return 17;
    case 6: // Saturday
      return 13;
    default: // Monday – Thursday (left at 20:00 to keep current behaviour)
      return 20;
  }
}

function isCallCentreOpen(date) {
  return getCallCentreCloseHour(date) != null;
}

/**
 * Returns the next date (starting from the day after `fromDate`) on which the
 * call centre is open. This lets "Call Me Tomorrow" skip closed days – e.g. a
 * Saturday selection rolls over Sunday to land on Monday, and a public
 * holiday that falls on a weekday (or Christmas Day / New Year's Day) is
 * skipped the same way.
 */
function getNextOperatingDay(fromDate) {
  const date = new Date(fromDate);
  do {
    date.setDate(date.getDate() + 1);
  } while (!isCallCentreOpen(date));
  return date;
}

/**
 * Parses the start hour (24h) of a slot string such as "08:00 - 10:00".
 * Returns `null` when the string has no parsable start time.
 */
function getSlotStartHour(slot) {
  const m = slot.match(/^\s*(\d{2}):\d{2}/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Parses the end hour (24h) of a slot string such as "08:00 - 10:00".
 * Returns `null` when the string has no parsable end time.
 */
function getSlotEndHour(slot) {
  const m = slot.match(/-\s*(\d{2}):\d{2}\s*$/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Trims a slot's end down to the close hour when its window runs past close,
 * e.g. "12:00 - 14:00" with a 13:00 close becomes "12:00 - 13:00".
 */
function clampSlotToCloseHour(slot, closeHour) {
  const endHour = getSlotEndHour(slot);
  if (endHour == null || endHour <= closeHour) return slot;
  const close = `${String(closeHour).padStart(2, '0')}:00`;
  return slot.replace(/-\s*\d{2}:\d{2}\s*$/, `- ${close}`);
}

/**
 * Filters slots to those the call centre can honour and trims any slot that
 * straddles the close time. Keeps slots that start before `closeHour` and whose
 * window ends after `notBeforeHour` (used to drop already-elapsed slots today).
 */
function buildSlotsWithinHours(slots, closeHour, notBeforeHour = -Infinity) {
  if (closeHour == null) return [];
  return slots.reduce((acc, opt) => {
    const startHour = getSlotStartHour(opt);
    const endHour = getSlotEndHour(opt);
    // Unparsable slot – keep as-is rather than silently dropping it.
    if (startHour == null || endHour == null) {
      acc.push(opt);
      return acc;
    }
    if (startHour >= closeHour) return acc; // starts after close
    if (endHour <= notBeforeHour) return acc; // window already elapsed
    acc.push(clampSlotToCloseHour(opt, closeHour));
    return acc;
  }, []);
}

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Returns true when two dates fall on the same calendar day.
 */
function isSameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Label for the "next open day" contact-window option. Reads "Call Me Tomorrow"
 * when the next open day is literally tomorrow, otherwise names the day (e.g.
 * "Call Me Monday" when a Saturday/Sunday selection rolls over to Monday).
 */
function getNextDayCallOptionLabel(fromDate) {
  const nextDay = getNextOperatingDay(fromDate);
  const literalTomorrow = new Date(fromDate);
  literalTomorrow.setDate(fromDate.getDate() + 1);

  return isSameCalendarDay(nextDay, literalTomorrow)
    ? 'Call Me Tomorrow'
    : `Call Me ${WEEKDAY_NAMES[nextDay.getDay()]}`;
}

/**
 * Recognises any "next open day" contact-window option, whether it reads
 * "Call Me Tomorrow" or names a specific weekday (e.g. "Call Me Monday").
 */
function isNextDayCallOption(callOption) {
  return (
    callOption === 'Call Me Tomorrow' ||
    WEEKDAY_NAMES.some((day) => callOption === `Call Me ${day}`)
  );
}

/**
 * Builds a time-aware copy of the contact-window-request modal data.
 *
 * Rules applied:
 *  1. Outside the current day's operating hours → remove "Call Me Right Now".
 *  2. "Call Me Later Today" → only show slots that have not yet ended AND that
 *     finish within today's operating hours. If none remain, the option is
 *     removed (e.g. a Saturday after 13:00, or any time on Sunday).
 *  3. "Call Me Tomorrow" → show slots that finish within the operating hours of
 *     the next open day (skipping closed days such as Sunday), so a callback can
 *     never be scheduled outside the call centre hours.
 *
 * The original `contact_time` field is replaced by two conditional fields:
 *   • `contact_time_today`    – visible when "Call Me Later Today" is selected
 *   • `contact_time_tomorrow` – visible when "Call Me Tomorrow" is selected
 */
export function buildContactWindowData(baseData, partnerName) {
  const data = JSON.parse(JSON.stringify(baseData));

  if (partnerName) {
    data.title = `When can ${partnerName} call you?`;
  }

  const now = getNow();
  const currentHour = now.getHours();
  const todayCloseHour = getCallCentreCloseHour(now);
  const isBusinessHours =
    todayCloseHour != null &&
    currentHour >= CALL_CENTRE_OPEN_HOUR &&
    currentHour < todayCloseHour;

  // Label for the "next open day" option – reads "Call Me Tomorrow" normally,
  // but names the day (e.g. "Call Me Monday") when the next open day is not
  // literally tomorrow (a weekend selection that rolls over to Monday).
  const nextDayLabel = getNextDayCallOptionLabel(now);

  const group = data.questions?.[0];
  if (!group) return data;

  const contactWindowQ = group.contact_window;
  const contactTimeQ = group.contact_time;

  // --- Rule 1: remove "Call Me Right Now" outside business hours ---
  if (contactWindowQ && !isBusinessHours) {
    contactWindowQ.options = contactWindowQ.options.filter(
      (opt) => opt !== 'Call Me Right Now'
    );
    if (contactWindowQ.value === 'Call Me Right Now') {
      contactWindowQ.value = '';
    }
  }

  // Rename the "Call Me Tomorrow" option to reflect the actual next open day.
  if (contactWindowQ) {
    contactWindowQ.options = contactWindowQ.options.map((opt) =>
      opt === 'Call Me Tomorrow' ? nextDayLabel : opt
    );
    if (contactWindowQ.value === 'Call Me Tomorrow') {
      contactWindowQ.value = nextDayLabel;
    }
  }

  // --- Rules 2 & 3: split contact_time into today / tomorrow ---
  if (contactTimeQ) {
    const allSlots = contactTimeQ.options;

    // For today: keep slots that are still in the future and within today's
    // operating hours, trimming the boundary slot to the close time. If the
    // centre is closed today, keep none.
    const todaySlots = buildSlotsWithinHours(
      allSlots,
      todayCloseHour,
      currentHour
    );

    // For tomorrow: keep slots within the operating hours of the next open day
    // (Sunday is skipped, rolling over to Monday), trimming the boundary slot.
    const tomorrowCloseHour = getCallCentreCloseHour(getNextOperatingDay(now));
    const tomorrowSlots = buildSlotsWithinHours(allSlots, tomorrowCloseHour);

    // If no today-slots remain, also remove "Call Me Later Today"
    if (todaySlots.length === 0 && contactWindowQ) {
      contactWindowQ.options = contactWindowQ.options.filter(
        (opt) => opt !== 'Call Me Later Today'
      );
    }

    // Replace original contact_time with two conditional fields
    delete group.contact_time;

    if (todaySlots.length > 0) {
      group.contact_time_today = {
        ...contactTimeQ,
        orderNumber: contactTimeQ.orderNumber,
        options: todaySlots,
        showIf: { key: 'contact_window', equals: 'Call Me Later Today' },
      };
    }

    group.contact_time_tomorrow = {
      ...contactTimeQ,
      orderNumber: (contactTimeQ.orderNumber ?? 2) + 0.1,
      options: tomorrowSlots,
      showIf: { key: 'contact_window', equals: nextDayLabel },
    };
  }

  // Default the contact_window selection to the first available option
  if (contactWindowQ && contactWindowQ.options.length > 0) {
    contactWindowQ.value = contactWindowQ.options[0];

    // Default the visible time dropdown to its first option; leave the other blank
    const defaultWindow = contactWindowQ.value;
    if (group.contact_time_today) {
      group.contact_time_today.value =
        defaultWindow === 'Call Me Later Today'
          ? group.contact_time_today.options[0]
          : '';
    }
    if (group.contact_time_tomorrow) {
      group.contact_time_tomorrow.value =
        defaultWindow === nextDayLabel
          ? group.contact_time_tomorrow.options[0]
          : '';
    }
  }

  return data;
}

export function deriveCallBackTime(callOption, timeRange) {
  const now = getNow();
  // "Tomorrow" resolves to the next day the call centre is open, so callbacks
  // never land on a closed day (e.g. a Saturday selection rolls over to Monday).
  const tomorrow = getNextOperatingDay(now);

  const addHours = (date, hours) => {
    const result = new Date(date);
    result.setHours(result.getHours() + hours);
    return result;
  };

  const getLowerBoundTime = (rangeString) => {
    const match = rangeString?.match(/^(\d{1,2}):?(\d{2})?/); // support "10:00" or "10"
    if (!match) return '09:00';
    const hours = match[1].padStart(2, '0');
    const minutes = match[2] ?? '00';
    return `${hours}:${minutes}`;
  };

  const getDateTimeString = (date, time = null) => {
    if (time) {
      const [hours, minutes] = time.split(':');
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    }

    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const HH = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');

    return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}.${ms}`;
  };

  if (callOption === 'Call Me Right Now') {
    return getDateTimeString(now);
  }

  if (callOption === 'Call Me Later Today') {
    if (!timeRange) {
      return getDateTimeString(addHours(now, 2));
    } else {
      const lowerBound = getLowerBoundTime(timeRange);
      return getDateTimeString(now, lowerBound);
    }
  }

  if (isNextDayCallOption(callOption)) {
    if (!timeRange) {
      return getDateTimeString(tomorrow, '08:00');
    } else {
      const lowerBound = getLowerBoundTime(timeRange);
      return getDateTimeString(tomorrow, lowerBound);
    }
  }

  return '';
}

export function mapFormDataToCarQuoteApiStructure(formData) {
  const {
    fullName,
    idNumber,
    phone,
    email,
    maritalStatus,
    carDetails,
    vehicleUsage,
    nightAddress,
    nightSecurity,
    dayAddress,
    daySecurity,
  } = formData;

  // Person (applicant) always comes from the applicant fields
  const [applicantFirstName, ...applicantLastParts] = (fullName || '').split(
    ' '
  );
  const applicantLastName = applicantLastParts.join(' ') || '';

  const { dateOfBirth: applicantDOB, gender: applicantGender } =
    deriveDateOfBirthAndGender(idNumber);

  // Choose the driver source (mainDriverDetails | applicant | additional)
  const driver = pickDriverSource(formData);

  const applicantFull = normalizeName(formData?.fullName || '');
  const driverFull = normalizeName(
    `${driver.firstName || ''} ${driver.lastName || ''}`
  );
  const isApplicantRegularDriver =
    applicantFull && driverFull && applicantFull === driverFull;

  return {
    Person: {
      IDNumber: idNumber || '',
      FirstName: applicantFirstName || '',
      LastName: applicantLastName || '',
      CellNumber: stripSpaces(phone),
      Email: email || '',
      HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
      DateofBirth: applicantDOB,
      Gender: applicantGender,
      MaritalStatus: maritalStatus || '',
    },
    Parking: {
      FullAddressDay: dayAddress || '',
      DayParkingType: getParkingValueByText(daySecurity) || '',
      FullAddressNight: nightAddress || '',
      NightParkingType: getParkingValueByText(nightSecurity) || '',
    },
    VehicleDetails: {
      VehicleYear: carDetails?.VehicleYear || '',
      VehicleDescription: carDetails?.SearchDescription
        ? carDetails.SearchDescription.toUpperCase()
        : '',
      VehicleKey: carDetails?.VehicleKey || '',

      // DateLicenceIssued comes from the chosen driver source
      DateLicenceIssued: driver.licenseFirstIssue
        ? fmtYYYYMMtoYYYYMMDD01(driver.licenseFirstIssue)
        : '',

      CoverRequired: formData.coverType || 'Comprehensive',

      VehicleUse: mapVehicleUseCode(vehicleUsage),

      // Year answers based on chosen driver source
      YearsUninterruptedInsurance: mapInsuranceYearsCode(
        driver.uninterruptedCarInsurance
      ),
      LastInsuranceClaim: mapInsuranceYearsCode(driver.lastClaimYears),

      // NCB based on chosen source
      NCB: calculateNCB(
        driver.uninterruptedCarInsurance,
        driver.lastClaimYears
      ),

      VehicleDriver: {
        FirstName: driver.firstName,
        LastName: driver.lastName,
        Gender: driver.gender,
        DateOfBirth: driver.dateOfBirth,
        MaritalStatus: driver.maritalStatus,
        IsApplicantRegularDriver: isApplicantRegularDriver,
      },
    },
  };
}

export function mapFormDataToFuneralQuoteApiStructure(formData) {
  const {
    firstName,
    surname,
    fullName,
    phone,
    email,
    maritalStatus,
    funeralCoverAmount,
    salaryRange,
    selectedCoverRangeOverride,
  } = formData;

  // Names — prefer a single fullName field, otherwise fall back to the
  // separate firstName / surname fields.
  const trimmedFullName = String(fullName || '').trim();
  let applicantFirstName;
  let applicantLastName;
  if (trimmedFullName) {
    const [firstPart, ...lastParts] = trimmedFullName.split(/\s+/);
    applicantFirstName = firstPart;
    applicantLastName = lastParts.join(' ');
  } else {
    applicantFirstName = String(firstName || '').trim();
    applicantLastName = String(surname || '').trim();
  }

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = formData.gender || ''; // e.g., "Male"

  let minCoverValue, maxCoverValue;
  if (selectedCoverRangeOverride) {
    minCoverValue = selectedCoverRangeOverride.min;
    maxCoverValue = selectedCoverRangeOverride.max;
  } else {
    const matches = funeralCoverAmount.match(/\d[\d\s]*/g);
    minCoverValue = parseInt(matches[0].replace(/\s/g, ''), 10);
    maxCoverValue = parseInt(matches[1].replace(/\s/g, ''), 10);
  }

  return {
    FirstName: applicantFirstName,
    LastName: applicantLastName,
    CellNumber: stripSpaces(phone),
    Email: email,
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Dateofbirth: applicantDOB,
    Gender: applicantGender,
    MaritalStatus: maritalStatus,
    MinCoverValue: minCoverValue,
    MaxCoverValue: maxCoverValue,
    Income: normalizeIncomeBandValue(salaryRange),
  };
}

function normalizeIncomeBandValue(text) {
  const matches = text.match(/\d[\d\s]*/g);

  if (!matches) return null;

  const numbers = matches.map((n) => parseInt(n.replace(/\s/g, ''), 10));

  if (numbers.length === 1) {
    if (text.includes('<')) {
      return numbers[0];
    }
    if (text.includes('+')) {
      return numbers[0] + 10000;
    }
    return numbers[0];
  }

  return numbers[numbers.length - 1];
}

const pad2 = (n) => String(n).padStart(2, '0');

function dobFromBirthday({ birthday } = {}) {
  if (!birthday) return '';
  const { year, month, day } = birthday;
  if (!year || !month || !day) return '';
  return `${year}/${pad2(month)}/${pad2(day)}`; // YYYY/MM/DD
}

export function mapFormDataToLifeQuoteApiStructure(formData) {
  const {
    firstName,
    surname,
    fullName,
    phone,
    email,
    maritalStatus,
    educationLevel,
    grossMonthlyIncome,
    lifeCoverValue,
    smoker,
    selectedCoverValueOverride,
  } = formData;

  // Names — prefer a single fullName field, otherwise fall back to the
  // separate firstName / surname fields.
  const trimmedFullName = String(fullName || '').trim();
  let first;
  let last;
  if (trimmedFullName) {
    const [firstPart, ...lastParts] = trimmedFullName.split(/\s+/);
    first = firstPart;
    last = lastParts.join(' ');
  } else {
    first = String(firstName || '').trim();
    last = String(surname || '').trim();
  }

  // Cover value (e.g., "R500 000.00" -> 500000)
  const cleanedCoverValue = String(lifeCoverValue || '').replace(
    /[^0-9.]/g,
    ''
  );
  const coverValueNum = Math.round(parseFloat(cleanedCoverValue));
  const selectedCoverValue =
    !isNaN(coverValueNum) && coverValueNum > 0 ? coverValueNum : 1000000;

  // Education (no spaces)
  const cleanedEducationLevel = String(educationLevel || '').replace(
    /\s+/g,
    ''
  );

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = formData.gender || ''; // e.g., "Male"

  const smokerCode = String(smoker || '')
    .toLowerCase()
    .includes('yes')
    ? 'T'
    : 'F';

  return {
    FirstName: first || '',
    LastName: last || '',
    CellNumber: stripSpaces(phone),
    Email: email || '',
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Dateofbirth: applicantDOB, // "1990/06/29"
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    EducationLevel: cleanedEducationLevel,
    MonthlyIncome: grossMonthlyIncome || '',
    SelectedCoverValue: Number.isFinite(selectedCoverValueOverride)
      ? selectedCoverValueOverride
      : selectedCoverValue,
    SmokeCode: smokerCode,
  };
}

export function mapFormDataToMedicalGapQuoteApiStructure(formData) {
  const {
    firstName,
    surname,
    phone,
    email,
    maritalStatus,
    gender,
    gapCoverType,
    coverFamily,
  } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  let coverType = 'basic';
  if (gapCoverType === 'Comprehensive Cover') {
    coverType = 'comprehensive';
  } else if (gapCoverType === 'Intermediate Cover') {
    coverType = 'intermediate';
  }

  let coverFor = 7; // 7 is for "Me and my family". 1 is for "Just me"
  if (coverFamily === 'Just me') {
    coverFor = 1;
  }

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    CoverFor: coverFor,
    CoverType: coverType || '',
  };
}

export function mapFormDataToMedicalAidQuoteApiStructure(formData) {
  const {
    firstName,
    surname,
    phone,
    email,
    maritalStatus,
    gender,
    adultsCoveredMedicalAid,
    childrenCoveredMedicalAid,
    hospitalChoice,
    treatmentsCovered,
    dayTreatmentsCovered,
    filters,
  } = formData;

  const applicantDOB = dobFromBirthday(formData);
  const numberOfAdults = parseInt(adultsCoveredMedicalAid, 10) || 1;
  const numberOfChildren = parseInt(childrenCoveredMedicalAid, 10) || 0;

  // HospitalChoice: journey value sets the base, filter overrides if present
  let hospitalChoiceValue = 'any';
  if (hospitalChoice === 'Network Hospital') {
    hospitalChoiceValue = 'network';
  }
  if (
    filters?.hospitalChoice === 'Network' ||
    filters?.hospitalChoice === 'Limited Network'
  ) {
    hospitalChoiceValue = 'network';
  } else if (filters?.hospitalChoice === 'Any Private') {
    hospitalChoiceValue = 'any';
  }

  // ExtraHospitalCover: from Q4 treatmentsCovered
  let extraHospitalCover = 'standard';
  if (treatmentsCovered === 'Fewer Exclusions') {
    extraHospitalCover = 'extended';
  }

  // LevelDayToDayCover: journey value sets the base, filter overrides if present
  let levelDayToDayCover = 4;
  if (dayTreatmentsCovered === 'Hospital cover only') {
    levelDayToDayCover = 1;
  } else if (dayTreatmentsCovered === 'Some day-to-day cover') {
    levelDayToDayCover = 2;
  } else if (dayTreatmentsCovered === 'Comprehensive day-to-day cover') {
    levelDayToDayCover = 4;
  }
  if (filters?.levelDayToDayCover === 'Hospital Only') {
    levelDayToDayCover = 1;
  } else if (filters?.levelDayToDayCover === 'Some Treatment') {
    levelDayToDayCover = 2;
  } else if (filters?.levelDayToDayCover === 'Full Cover') {
    levelDayToDayCover = 4;
  }

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB,
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: gender || '',
    MaritalStatus: maritalStatus || '',
    NumAdults: numberOfAdults,
    NumChildren: numberOfChildren,
    HospitalChoice: hospitalChoiceValue,
    ExtraHospitalCover: extraHospitalCover,
    LevelDayToDayCover: levelDayToDayCover,
  };
}

export function mapFormDataToMedicalAidResultsToggleApiStructure(formData) {
  const {
    firstName,
    surname,
    phone,
    email,
    maritalStatus,
    gender,
    adultsCoveredMedicalAid,
    childrenCoveredMedicalAid,
    hospitalChoice,
    dayTreatmentsCovered,
    filters,
  } = formData;

  const applicantDOB = dobFromBirthday(formData);
  const numberOfAdults = parseInt(adultsCoveredMedicalAid, 10) || 1;
  const numberOfChildren = parseInt(childrenCoveredMedicalAid, 10) || 0;

  let hospitalChoiceValue = 'any';
  if (hospitalChoice === 'Network Hospital') {
    hospitalChoiceValue = 'network';
  }
  if (filters?.hospitalChoice === 'Network') {
    hospitalChoiceValue = 'network';
  } else if (filters?.hospitalChoice === 'Limited Network') {
    hospitalChoiceValue = 'limited';
  } else if (filters?.hospitalChoice === 'Any Private') {
    hospitalChoiceValue = 'any';
  }

  let levelDayToDayCover = 4;
  if (dayTreatmentsCovered === 'Hospital cover only') {
    levelDayToDayCover = 1;
  } else if (dayTreatmentsCovered === 'Some day-to-day cover') {
    levelDayToDayCover = 2;
  } else if (dayTreatmentsCovered === 'Comprehensive day-to-day cover') {
    levelDayToDayCover = 4;
  }
  if (filters?.levelDayToDayCover === 'Hospital Only') {
    levelDayToDayCover = 1;
  } else if (filters?.levelDayToDayCover === 'Some Treatment') {
    levelDayToDayCover = 2;
  } else if (filters?.levelDayToDayCover === 'Full Cover') {
    levelDayToDayCover = 4;
  }

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB,
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: gender || '',
    MaritalStatus: maritalStatus || '',
    NumAdults: numberOfAdults,
    NumChildren: numberOfChildren,
    HospitalChoice: hospitalChoiceValue,
    LevelDayToDayCover: levelDayToDayCover,
  };
}

export function mapFormDataToMedicalInsuranceQuoteApiStructure(formData) {
  const {
    firstName,
    surname,
    phone,
    email,
    maritalStatus,
    gender,
    medicalInsuranceSpouse,
    medicalInsuranceNumberOfKids,
  } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  let numAdults = 1; // 1 is for "Just me", 2 is for "Me and my spouse"
  if (medicalInsuranceSpouse === 'Me and my spouse') {
    numAdults = 2;
  }

  let numChildren = 0;
  if (medicalInsuranceNumberOfKids === 'One') {
    numChildren = 1;
  } else if (medicalInsuranceNumberOfKids === 'Two') {
    numChildren = 2;
  } else if (medicalInsuranceNumberOfKids === 'Three') {
    numChildren = 3;
  } else if (medicalInsuranceNumberOfKids === 'Four') {
    numChildren = 4;
  } else if (medicalInsuranceNumberOfKids === 'Five') {
    numChildren = 5;
  }

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    NumAdults: numAdults,
    NumChildren: numChildren,
  };
}

export function mapFormDataToPetInsuranceQuoteApiStructure(formData) {
  const { firstName, surname, phone, email, maritalStatus, gender, pets } =
    formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"

  let myPetDetailsArray = [];
  if (pets && Array.isArray(pets)) {
    pets.forEach((pet) => {
      let petType = 0;
      if (pet.type === 'Dog') {
        petType = 1;
      } else if (pet.type === 'Cat') {
        petType = 2;
      }
      myPetDetailsArray.push({
        PetName: pet.name || '',
        PetType2_0: petType,
        PetGender: pet.gender || '',
        PetBreed: pet.breed || '',
        PetColour: pet.colour || '',
      });
    });
  }

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    MyPetDetails: myPetDetailsArray,
  };
}

export function mapFormDataToTravelInsuranceQuoteApiStructure(formData) {
  const { firstName, surname, phone, email, maritalStatus, gender } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"

  const travelDestinationsArray = formData.travelCountries || [];
  const travelDepartureDate =
    (formData.travelCoverDates && formData.travelCoverDates[0]) || '';
  const travelReturnDate =
    (formData.travelCoverDates && formData.travelCoverDates[1]) || '';
  const numberOfTravellers =
    parseInt(formData.numberOfPeopleTraveling, 10) + 1 || 1;
  const myTravelDetails = {
    TravelDestinations: travelDestinationsArray,
    TravelDepartureDate: travelDepartureDate,
    TravelReturnDate: travelReturnDate,
    NumberOfTravellers: numberOfTravellers,
  };
  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    MyTravelDetails: myTravelDetails,
  };
}

export function mapFormDataToScratchDentCoverQuoteApiStructure(formData) {
  const { firstName, surname, phone, email, maritalStatus, gender } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"

  const carDetails = formData.carDetails || {};
  const vehicleDetails = {
    VehicleYear: carDetails.VehicleYear,
    VehicleDescription: carDetails.SearchDescription,
    VehicleKey: carDetails.VehicleKey,
  };
  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    sDVehicle: vehicleDetails,
  };
}

export function mapFormDataToVehicleTrackingQuoteApiStructure(formData) {
  const { firstName, surname, phone, email, maritalStatus, gender } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  const currentlyInsured = formData.carInsurance === 'Yes';

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    CurrentlyInsured: currentlyInsured,
  };
}

export function mapFormDataToDebtCounsellingOffersApiStructure(formData) {
  const { fullName, phone, email, maritalStatus, gender } = formData;

  const name = splitName(fullName || '');
  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  return {
    FirstName: name.first || '',
    LastName: name.last || '',
    IDNumber: formData.idNumber || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
  };
}

export function mapFormDataToBusinessInsuranceQuoteApiStructure(formData) {
  const {
    businessName,
    industryCategory,
    totalAssest10Million,
    moreThan10Vehicles,
    professionalIndemnity,
    publicLiablity,
    businessPersonFirstName,
    businessPersonSurname,
    hippoMarketingOptIn,
    businessPersonPhone,
    businessPersonEmail,
  } = formData;

  return {
    BusinessName: businessName,
    BusinessType: industryCategory.Pk,
    SumInsuredAssets: totalAssest10Million === 'Yes' ? '10000001' : '1',
    NumVehicles: moreThan10Vehicles === 'Yes' ? '11' : '1',
    ProfessionalIndemnityCover: professionalIndemnity === 'Yes' ? true : false,
    HippoMarketingOptin: hippoMarketingOptIn ?? true,
    PublicLiabilityCover: publicLiablity === 'Yes' ? true : false,
    ContactFirstName: businessPersonFirstName || '',
    ContactLastName: businessPersonSurname || '',
    ContactEmail: businessPersonEmail || '',
    ContactCellNumber: stripSpaces(businessPersonPhone),
  };
}

export function mapFormDataToBuildingInsuranceQuoteApiStructure(formData) {
  const {
    fullName,
    phone,
    email,
    maritalStatus,
    gender,
    numberOfGeysers,
    homeValue,
    idNumber,
    buildingAddress,
  } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  const name = splitName(fullName || '');

  // Add additional mapping requirements here as needed
  return {
    Person: {
      IDNumber: idNumber,
      FirstName: name.first || '',
      LastName: name.last || '',
      CellNumber: stripSpaces(phone),
      Email: email || '',
      HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
      DateofBirth: applicantDOB,
      Gender: applicantGender,
      MaritalStatus: maritalStatus || '',
      FullAddress: buildingAddress,
    },
    BuildingDetails: {
      BuildingValue: Number(homeValue) || 0,
      NumberOfGeysers: numberOfGeysers,
    },
  };
}

export function mapFormDataToHomeContentsInsuranceQuoteApiStructure(formData) {
  const {
    idNumber,
    phone,
    email,
    maritalStatus,
    gender,
    homeAddress,
    moveIntoHome,
    homeBreakIns,
    breakInHappen,
    homeContentValue,
    extraSecurityFeatures,
    occupiedDuringDay,
    homeBordered,
  } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  const name = splitName(formData.fullName || '');

  // Add additional mapping requirements here as needed
  return {
    Person: {
      IDNumber: idNumber,
      FirstName: name.first,
      LastName: name.last,
      CellNumber: stripSpaces(phone),
      Email: email || '',
      HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
      DateofBirth: applicantDOB,
      Gender: applicantGender,
      MaritalStatus: maritalStatus || '',
      FullAddress: homeAddress,
    },
    HomeContentsDetails: {
      HomeContentsValue: Number(homeContentValue) || 0,
      MoveInYear: Number(moveIntoHome.year) || 0,
      MoveInMonth: Number(moveIntoHome.month) || 0,
      HadBreakIn: homeBreakIns === 'Yes' ? true : false,
      BurglaryYear: Number(breakInHappen?.year) || 0,
      BurglaryMonth: Number(breakInHappen?.month) || 0,
      AlarmWorking: extraSecurityFeatures.includes('Alarm'),
      // AlarmLinkedToArmedResponse: extraSecurityFeatures.includes('Alarm'),
      HasElectricFence: extraSecurityFeatures.includes('Electric fence'),
      // ElectricFenceArmed: extraSecurityFeatures.includes('Electric fence'),
      OccupiedDuringWorkingHours: occupiedDuringDay === 'Yes' ? true : false,
      NeighboursOnAllBorders: homeBordered === 'Yes' ? true : false,
      BurglarBarsOnAllWindows: extraSecurityFeatures.includes(
        'Burglar bars on all windows'
      ),
      InAccessControlledEstate: extraSecurityFeatures.includes(
        'Access controlled estate'
      ),
    },
  };
}

export function mapFormDataToTyreAndRimInsuranceQuoteApiStructure(formData) {
  const { firstName, surname, phone, email, maritalStatus, gender } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"

  let rimType = 'S';
  if (formData.rimType !== 'Standard rims') {
    rimType = 'A';
  }
  const carDetails = formData.carDetails || {};
  const vehicleDetails = {
    VehicleYear: carDetails.VehicleYear,
    VehicleDescription: carDetails.SearchDescription,
    VehicleKey: carDetails.VehicleKey,
    RimType: rimType,
  };

  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    FullAddress: formData.address || '',
    TRVehicle: vehicleDetails,
  };
}

export function mapFormDataToMotorWarrantyQuoteApiStructure(formData) {
  const { firstName, surname, phone, email, maritalStatus, gender } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"

  const IsWarrantyActive = formData.carWarranty === 'Yes';
  const isServiceHistoryUpToDate = formData.carServicePlan === 'Yes';
  const currentMileage = parseInt(formData.carMileage, 10) || 0;

  const vehicleYear = formData.carDetails?.VehicleYear
    ? parseInt(formData.carDetails.VehicleYear, 10)
    : new Date().getFullYear();
  const yearsSinceVehicle = Math.max(1, new Date().getFullYear() - vehicleYear);

  const KmPerYear = Math.round(currentMileage / yearsSinceVehicle);
  const carDetails = formData.carDetails || {};

  const vehicleDetails = {
    VehicleYear: carDetails.VehicleYear,
    VehicleDescription: carDetails.SearchDescription,
    VehicleKey: carDetails.VehicleKey,

    IsWarrantyActive: IsWarrantyActive,
    IsServiceHistoryUpToDate: isServiceHistoryUpToDate,
    CurrentMileage: currentMileage,
    KilometresPerYear: KmPerYear,
  };
  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    FullAddress: formData.address || '',
    MWVehicle: vehicleDetails,
  };
}

export function mapFormDataToServicePlanQuoteApiStructure(formData) {
  const {
    firstName,
    surname,
    phone,
    email,
    maritalStatus,
    gender,
    carDetails,
  } = formData;

  const applicantDOB = dobFromBirthday(formData); // "YYYY/MM/DD"
  const applicantGender = gender || ''; // e.g., "Male"
  const vehicleDetails = {
    VehicleYear: carDetails.VehicleYear,
    VehicleDescription: carDetails.SearchDescription,
    VehicleKey: carDetails.VehicleKey,
  };

  // Add additional mapping requirements here as needed
  return {
    FirstName: firstName || '',
    LastName: surname || '',
    DateOfBirth: applicantDOB, // "1990/06/29"
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: formData.hippoMarketingOptIn ?? true,
    Gender: applicantGender, // "Male"
    MaritalStatus: maritalStatus || '',
    SPVehicle: vehicleDetails,
  };
}

export function mapFormDataToEmployeeBenefitCallBackStructure(formData) {
  // Add additional mapping requirements here as needed
  return {
    ContactName: formData.businessPersonFirstName || '',
    ContactSurname: formData.businessPersonSurname || '',
    ContactEmailAddress: formData.businessPersonEmail || '',
    ContactCellNumber: stripSpaces(formData.businessPersonPhone),
    HippoMarketingOptIn: formData.hippoMarketingOptIn ?? true,
    StaffCount: formData.medicalInsuranceNumberOfEmployees || '0 - 20',
    BusinessName: formData.businessName || '',
    RequestFor: formData.whyBusinessMedicalAid,
    CallBackTime: formData.callBackTime || '',
  };
}

export function mapFormDataToLoanTier1QuoteApiStructure(formData) {
  const { fullName, phone, email, idNumber, hippoMarketingOptIn } = formData;

  const name = splitName(fullName || '');

  return {
    FirstName: name.first,
    LastName: name.last,
    IDNumber: idNumber || '',
    Email: email || '',
    CellNumber: stripSpaces(phone),
    HippoMarketingOptin: hippoMarketingOptIn ?? true,
  };
}

export function mapFormAndQuoteDataToLoanTier1QuoteAcceptApiStructure(
  quoteData
) {
  const { fullName, idNumber, email, phone } = quoteData.formValues || {};
  const { BrandName, Pk, MessageId } = quoteData;
  const name = splitName(fullName || '');
  return {
    IDNumber: idNumber || '',
    FirstName: name.first || '',
    LastName: name.last || '',
    CellNumber: stripSpaces(phone),
    Email: email || '',
    BrandName: BrandName || '',
    QuotePK: Pk || '',
    msgid: MessageId || '',
  };
}

export function mapFormDataToFibreQuoteApiStructure(formData) {
  const { latitude, longitude } = formData;
  return {
    latitude: latitude,
    longitude: longitude,
  };
}

export function mapFormAndQuoteDataToFibreQuoteAcceptApiStructure(quoteData) {
  const {
    fullName,
    firstName,
    surname,
    idNumber,
    email,
    phone,
    CallBackTime,
    fibreAddress,
    streetName,
    suburb,
    complexName,
    unitNumber,
    town,
    postalCode,
    latitude,
    longitude,
  } = quoteData.formValues || {};
  const { id, quoteid, packageid } = quoteData;
  const name = splitName(fullName || '');
  return {
    FirstName: name.first || firstName || '',
    LastName: name.last || surname || '',
    IDNumber: idNumber || '',
    CellNumber: stripSpaces(phone),
    Email: email || '',
    packageid: packageid || id || '',
    quoteid: quoteid || '',
    Address: fibreAddress || '',
    streetName: streetName || '',
    suburb: suburb || '',
    complexName: complexName || '',
    unitNumber: unitNumber || '',
    town: town || '',
    postalCode: postalCode || '',
    latitude: latitude || '',
    longitude: longitude || '',
    CallBackTime: CallBackTime || '',
  };
}

export function mapPersonDataToUpdatePersonApiStructure(personData) {
  const isTrueLike = (value) => {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' ||
        normalized === '1' ||
        normalized === 'yes' ||
        normalized === 'on'
      );
    }
    return false;
  };

  const rawMarketingChannelSMS =
    personData.marketingChannelSMS !== undefined
      ? isTrueLike(personData.marketingChannelSMS)
      : isTrueLike(personData.MarketingChannelSMS);
  const rawMarketingChannelWhatsApp =
    personData.marketingChannelWhatsApp !== undefined
      ? isTrueLike(personData.marketingChannelWhatsApp)
      : isTrueLike(personData.MarketingChannelWhatsApp);
  const rawMarketingChannelEmail =
    personData.marketingChannelEmail !== undefined
      ? isTrueLike(personData.marketingChannelEmail)
      : isTrueLike(personData.MarketingChannelEmail);
  const marketingCommsOn =
    personData.marketingCommunication !== undefined
      ? isTrueLike(personData.marketingCommunication)
      : isTrueLike(personData.MarketingCommunication);
  const marketingChannelSMS = rawMarketingChannelSMS;
  const marketingChannelWhatsApp = rawMarketingChannelWhatsApp;
  const marketingChannelEmail = rawMarketingChannelEmail;

  return {
    PersonPK: personData.personPK ?? personData.PersonPK ?? 0,
    FirstName: personData.firstName ?? personData.FirstName ?? '',
    LastName: personData.lastName ?? personData.LastName ?? '',
    CellNumber: stripSpaces(
      personData.cellNumber ?? personData.CellNumber ?? ''
    ),
    ...(personData.emailAddress !== undefined && {
      EmailAddress: personData.emailAddress ?? '',
    }),
    ...(personData.EmailAddress !== undefined && {
      EmailAddress: personData.EmailAddress ?? '',
    }),
    ...(personData.maritalStatus !== undefined && {
      MaritalStatus: personData.maritalStatus ?? '',
    }),
    ...(personData.MaritalStatus !== undefined && {
      MaritalStatus: personData.MaritalStatus ?? '',
    }),
    ...(personData.gender !== undefined && { Gender: personData.gender ?? '' }),
    ...(personData.Gender !== undefined && { Gender: personData.Gender ?? '' }),
    ...(personData.dateOfBirth !== undefined && {
      DateOfBirth: personData.dateOfBirth ?? null,
    }),
    ...(personData.DateOfBirth !== undefined && {
      DateOfBirth: personData.DateOfBirth ?? null,
    }),
    ...(personData.idNumber !== undefined && {
      IDNumber: personData.idNumber ?? '',
    }),
    ...(personData.IDNumber !== undefined && {
      IDNumber: personData.IDNumber ?? '',
    }),
    ...(personData.fullAddress !== undefined && {
      FullAddress: personData.fullAddress ?? '',
    }),
    ...(personData.FullAddress !== undefined && {
      FullAddress: personData.FullAddress ?? '',
    }),
    ...(personData.suburbPostalCode !== undefined && {
      SuburbPostalCode: personData.suburbPostalCode ?? '',
    }),
    ...(personData.SuburbPostalCode !== undefined && {
      SuburbPostalCode: personData.SuburbPostalCode ?? '',
    }),
    ...(personData.suburbName !== undefined && {
      SuburbName: personData.suburbName ?? '',
    }),
    ...(personData.SuburbName !== undefined && {
      SuburbName: personData.SuburbName ?? '',
    }),
    ...(personData.suburbSequenceNumber !== undefined && {
      SuburbSequenceNumber: personData.suburbSequenceNumber ?? 0,
    }),
    ...(personData.SuburbSequenceNumber !== undefined && {
      SuburbSequenceNumber: personData.SuburbSequenceNumber ?? 0,
    }),
    ...(personData.marketingCommunication !== undefined && {
      MarketingCommunication: marketingCommsOn,
    }),
    ...(personData.MarketingCommunication !== undefined && {
      MarketingCommunication: isTrueLike(personData.MarketingCommunication),
    }),
    ...(personData.marketingFrequency !== undefined && {
      MarketingFrequency: personData.marketingFrequency ?? '',
    }),
    ...(personData.MarketingFrequency !== undefined && {
      MarketingFrequency: personData.MarketingFrequency ?? '',
    }),
    ...(personData.marketingChannelSMS !== undefined && {
      MarketingChannelSMS: marketingChannelSMS,
    }),
    ...(personData.MarketingChannelSMS !== undefined && {
      MarketingChannelSMS: isTrueLike(personData.MarketingChannelSMS),
    }),
    ...(personData.marketingChannelWhatsApp !== undefined && {
      MarketingChannelWhatsApp: marketingChannelWhatsApp,
    }),
    ...(personData.MarketingChannelWhatsApp !== undefined && {
      MarketingChannelWhatsApp: isTrueLike(personData.MarketingChannelWhatsApp),
    }),
    ...(personData.marketingChannelEmail !== undefined && {
      MarketingChannelEmail: marketingChannelEmail,
    }),
    ...(personData.MarketingChannelEmail !== undefined && {
      MarketingChannelEmail: isTrueLike(personData.MarketingChannelEmail),
    }),
  };
}

export function mapVehicleDataToAddVehicleApiStructure(vehicleData) {
  return {
    VehicleMake: vehicleData.vehicleMake,
    VehicleModel: vehicleData.vehicleModel,
    VehicleKey: vehicleData.vehicleKey,
    VehicleYear: vehicleData.vehicleYear,
    PolicyHolderCellphoneNumber: vehicleData.policyHolderCellphoneNumber,
    VehicleDescription: vehicleData.vehicleDescription,
    NightTimeAddress: vehicleData.nightTimeAddress,
    NightTimeSecuritySetup: getParkingValueByText(
      vehicleData.nightTimeSecuritySetup
    ),
    DayTimeAddress: vehicleData.dayTimeAddress,
    DayTimeSecuritySetup: getParkingValueByText(
      vehicleData.dayTimeSecuritySetup
    ),
    CarUse: normalizeVehicleUse(vehicleData.carUse),
  };
}

export function mapDriverDataToAddVehicleDriverApiStructure(driverData) {
  return {
    DriverName: driverData.driverName,
    DriverLastName: driverData.driverLastName,
    DateOfBirth: driverData.dateOfBirth,
    PolicyHolderCellphoneNumber: driverData.policyHolderCellphoneNumber,
    MaritalStatus: driverData.maritalStatus,
    Gender: driverData.gender,
    DateLicenseObtained: driverData.dateLicenseObtained,
    LicenseType: driverData.licenseType,
    ComprehensiveInsDuration: driverData.comprehensiveInsDuration,
    LastClaimDuration: driverData.lastClaimDuration,
  };
}

export function mapFormDataToMotorcycleQuoteApiStructure(formData) {
  // Add additional mapping requirements here as needed
  const {
    idNumber,
    FirstName,
    LastName,
    phone,
    email,
    hippoMarketingOptIn,
    yearOfMotorCycleManufacturer,
    motorCycleDetails,
    bikeValue,
    nightAddress,
    nightSecurity,
    mainDriver,
    // mainDriverLicense,
    licenseFirstIssue,
    maritalStatus,
    uninterruptedBikeInsurance,
    //Additional
    additionalDriverFullName,
    additionalDriverGender,
    additionalDriverBirthday,
    additionalDriverMaritalStatus,
    additionalDriverLicenseFirstIssue,
    additionalDriverUninterruptedBikeInsurance,
  } = formData;

  const { dateOfBirth, gender } = deriveDateOfBirthAndGender(idNumber);
  const [applicantFirstName, ...applicantLastParts] = (
    additionalDriverFullName || ''
  ).split(' ');
  const applicantLastName = applicantLastParts.join(' ') || '';
  const addDob = additionalDriverBirthday
    ? `${String(additionalDriverBirthday.year || '').padStart(4, '0')}-${String(additionalDriverBirthday.month || '').padStart(2, '0')}-${String(additionalDriverBirthday.day || '').padStart(2, '0')}`
    : '';

  const MotorcycleDetailsArray = motorCycleDetails
    ? [
        {
          MotorBikeYear: yearOfMotorCycleManufacturer,
          MotorBikeDescription: motorCycleDetails.MotorcycleDescription,
          MotorBikeKey: motorCycleDetails.VehicleKey,
          MotorBikeEstimatedValue: Number(bikeValue),
          DateLicenceIssued:
            mainDriver === 'Yes'
              ? licenseFirstIssue
              : additionalDriverLicenseFirstIssue
                ? fmtYYYYMMtoYYYYMMDD01(
                    mainDriver === 'Yes'
                      ? licenseFirstIssue
                      : additionalDriverLicenseFirstIssue
                  )
                : '',
          CoverRequired: 'Individual',
          // The bike flow asks a single "years claim free" question, which is
          // already the NCB; we only normalize the "7+" scale value to "7".
          NCB: normalizeBikeNCB(
            mainDriver === 'Yes'
              ? uninterruptedBikeInsurance
              : additionalDriverUninterruptedBikeInsurance
          ),
          FullAddressDay: nightAddress || '',
          DayParkingType: getParkingValueByText(nightSecurity) || '',
          FullAddressNight: nightAddress || '',
          NightParkingType: getParkingValueByText(nightSecurity) || '',
          MotorBikeDriver: {
            FirstName: mainDriver === 'Yes' ? FirstName : applicantFirstName, //driver.firstName,
            LastName: mainDriver === 'Yes' ? LastName : applicantLastName, //driver.lastName,
            Gender: mainDriver === 'Yes' ? gender : additionalDriverGender, //driver.gender,
            DateOfBirth: mainDriver === 'Yes' ? dateOfBirth : addDob, //driver.dateOfBirth,
            MaritalStatus:
              mainDriver === 'Yes'
                ? maritalStatus
                : additionalDriverMaritalStatus, //driver.maritalStatus,
            IsApplicantRegularDriver: true,
          },
        },
      ]
    : [];
  return {
    Person: {
      IDNumber: idNumber || '',
      FirstName: FirstName || '',
      LastName: LastName || '',
      CellNumber: stripSpaces(phone),
      Email: email || '',
      HippoMarketingOptin: hippoMarketingOptIn ?? true,
      DateofBirth: dateOfBirth,
      Gender: gender,
      MaritalStatus:
        mainDriver === 'Yes' ? maritalStatus : additionalDriverMaritalStatus,
    },
    CycleDetails: MotorcycleDetailsArray,
  };
}

export function mapContactFormDataToApiStructure({
  firstName,
  lastName,
  phone,
  email,
  regarding,
  message,
}) {
  return {
    ContactName: firstName || '',
    ContactSurname: lastName || '',
    ContactCellNumber: stripSpaces(phone),
    ContactEmailAddress: email || '',
    Regarding: regarding || '',
    Message: message || '',
  };
}
